import React, { useReducer, useEffect, useMemo, useCallback, useRef, useState, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useOrderNotifications } from '../hooks/useOrderNotifications';
import { productionAssignmentsAPI, chefsAPI } from '../services/api';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Package,
  XCircle,
  Play,
  Check,
  Square,
  CheckSquare,
  AlertOctagon,
} from 'lucide-react';
import { LoadingSpinner } from '../components/UI/LoadingSpinner';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/formatDate';
import { ProductSearchInput, ProductDropdown } from './OrdersTablePage';

// ========= Types =========
type TaskStatus = 'pending' | 'assigned' | 'in_progress' | 'completed' | 'rejected';
type RejectReason = 'تالف' | 'لم يصل' | 'نفاد المخزون' | 'غير متاح' | 'أخرى' | '';

interface ChefTask {
  itemId: string;
  orderId: string;
  orderNumber: string;
  productId: string;
  productName: string;
  quantity: number;
  status: TaskStatus;
  rejected: boolean;
  rejectReason: RejectReason;
  createdAt: string;
  updatedAt: string;
  progress?: number;
  branchName?: string;
  branchId?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  department?: { _id: string; name: string };
  assignedTo?: { _id: string; username: string };
  unit: string;
}

interface State {
  tasks: ChefTask[];
  chefId: string | null;
  loading: boolean;
  error: string;
  submitting: Set<string>;
  submittingAll: boolean;
  socketConnected: boolean;
  filter: { status: string; search: string };
  page: number;
  totalPages: number;
}

type Action =
  | { type: 'SET_TASKS'; payload: { tasks: ChefTask[]; totalPages: number } }
  | { type: 'SET_CHEF_ID'; payload: string | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_SUBMITTING'; payload: { itemId: string; add: boolean } }
  | { type: 'SET_SUBMITTING_ALL'; payload: boolean }
  | { type: 'SET_SOCKET_CONNECTED'; payload: boolean }
  | { type: 'SET_FILTER'; payload: { status: string; search: string } }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'TASK_ASSIGNED'; payload: { orderId: string; items: any[]; orderNumber: string; branchName: string } }
  | {
      type: 'UPDATE_ITEM_STATUS';
      payload: {
        orderId: string;
        itemId: string;
        status: TaskStatus;
        updatedAt: string;
        rejected: boolean;
        rejectReason: RejectReason;
      };
    }
  | { type: 'UPDATE_ORDER_STATUS'; orderId: string; status: string };

const initialState: State = {
  tasks: [],
  chefId: null,
  loading: true,
  error: '',
  submitting: new Set(),
  submittingAll: false,
  socketConnected: false,
  filter: { status: 'all', search: '' },
  page: 1,
  totalPages: 1,
};

const CACHE_TTL = 5 * 60 * 1000;
const cache = new Map<string, { data: ChefTask[]; timestamp: number }>();

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_TASKS':
      return { ...state, tasks: action.payload.tasks, totalPages: action.payload.totalPages, loading: false };
    case 'SET_CHEF_ID':
      return { ...state, chefId: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'SET_SUBMITTING':
      const newSubmitting = new Set(state.submitting);
      if (action.payload.add) newSubmitting.add(action.payload.itemId);
      else newSubmitting.delete(action.payload.itemId);
      return { ...state, submitting: newSubmitting };
    case 'SET_SUBMITTING_ALL':
      return { ...state, submittingAll: action.payload };
    case 'SET_SOCKET_CONNECTED':
      return { ...state, socketConnected: action.payload };
    case 'SET_FILTER':
      return { ...state, filter: action.payload, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'TASK_ASSIGNED': {
      const items = Array.isArray(action.payload.items) ? action.payload.items : [];
      if (!items.length) return state;
      const newTasks = items
        .filter((item) => item.assignedTo?._id === state.chefId)
        .map((item) => ({
          itemId: item.itemId || 'unknown',
          orderId: action.payload.orderId || 'unknown',
          orderNumber: action.payload.orderNumber || 'N/A',
          productId: item.productId || 'unknown',
          productName: item.productName || 'Unknown Product',
          quantity: Number(item.quantity) || 1,
          status: (item.status as TaskStatus) || 'pending',
          rejected: item.rejected || false,
          rejectReason: (item.rejectReason || 'أخرى') as RejectReason,
          createdAt: item.createdAt || new Date().toISOString(),
          updatedAt: item.updatedAt || new Date().toISOString(),
          progress: getStatusInfo((item.status as TaskStatus) || 'pending').progress,
          branchName: action.payload.branchName || 'Unknown Branch',
          branchId: item.branchId || 'unknown',
          priority: item.priority || 'medium',
          department: item.department || { _id: 'unknown', name: 'Unknown Department' },
          assignedTo: item.assignedTo || { _id: 'unknown', username: 'Unknown Chef' },
          unit: item.unit || 'unit',
        }));
      const updatedTasks = [
        ...state.tasks.filter((t) => !newTasks.some((nt) => nt.itemId === t.itemId)),
        ...newTasks,
      ];
      return { ...state, tasks: updatedTasks, totalPages: Math.ceil(updatedTasks.length / 10) || 1 };
    }
    case 'UPDATE_ITEM_STATUS':
      return {
        ...state,
        tasks: state.tasks.map((task) =>
          task.itemId === action.payload.itemId && task.orderId === action.payload.orderId
            ? {
                ...task,
                status: action.payload.status,
                updatedAt: action.payload.updatedAt,
                progress: getStatusInfo(action.payload.status).progress,
                rejected: action.payload.rejected,
                rejectReason: action.payload.rejectReason,
              }
            : task
        ),
      };
    case 'UPDATE_ORDER_STATUS':
      return {
        ...state,
        tasks:
          action.status === 'completed' || action.status === 'cancelled' || action.status === 'rejected'
            ? state.tasks.filter((task) => task.orderId !== action.orderId)
            : state.tasks,
      };
    default:
      return state;
  }
};

const getStatusInfo = (status: TaskStatus) => {
  const statusMap: Record<TaskStatus, { label: string; color: string; icon: React.FC<{ className?: string }>; progress: number }> = {
    pending: { label: 'item_pending', color: 'bg-amber-100 text-amber-800', icon: Clock, progress: 0 },
    assigned: { label: 'item_assigned', color: 'bg-blue-100 text-blue-800', icon: Package, progress: 25 },
    in_progress: { label: 'item_in_progress', color: 'bg-blue-200 text-blue-800', icon: Package, progress: 50 },
    completed: { label: 'item_completed', color: 'bg-green-100 text-green-800', icon: CheckCircle, progress: 100 },
    rejected: { label: 'item_rejected', color: 'bg-red-100 text-red-800', icon: XCircle, progress: 0 },
  };
  return statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: Clock, progress: 0 };
};

// ========== OrderCard Component ==========
const OrderCard = memo(
  ({
    order,
    isRtl,
    t,
    submittingItems,
    submittingAll,
    chefId,
    socketConnected,
    language,
    handleUpdateItemStatus,
  }) => {
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [bulkStatus, setBulkStatus] = useState<TaskStatus | null>(null);

    const isSubmitting = (itemId: string) => submittingItems.has(itemId);

    const itemsByStatus = useMemo(() => {
      const groups = { pending: [], assigned: [], in_progress: [], completed: [], rejected: [] };
      order.items.forEach((item) => {
        const status = item.status || 'pending';
        if (groups[status]) groups[status].push(item);
      });
      return groups;
    }, [order.items]);

    const totalItems = order.items.length;
    const completedItems = itemsByStatus.completed.length;
    const rejectedItems = itemsByStatus.rejected.length;
    const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    const canUpdateItem = (item: any) =>
      item.assignedTo?.toString() === chefId && item.status !== 'completed' && item.status !== 'rejected';

    const toggleItemSelection = (itemId: string) => {
      setSelectedItems((prev) => {
        const newSet = new Set(prev);
        newSet.has(itemId) ? newSet.delete(itemId) : newSet.add(itemId);
        return newSet;
      });
    };

    const toggleSelectAll = () => {
      if (selectedItems.size === order.items.length) {
        setSelectedItems(new Set());
      } else {
        setSelectedItems(new Set(order.items.map((i) => i._id)));
      }
    };

    const openBulkModal = (status: TaskStatus) => {
      setBulkStatus(status);
      setShowBulkModal(true);
    };

    const closeBulkModal = () => {
      setShowBulkModal(false);
      setBulkStatus(null);
    };

    const executeBulkUpdate = () => {
      if (!bulkStatus) return;
      const itemsToUpdate = selectedItems.size > 0 ? Array.from(selectedItems) : order.items.map((i) => i._id);
      closeBulkModal();
      setSelectedItems(new Set());
      itemsToUpdate.forEach((itemId) => {
        handleUpdateItemStatus(order._id, itemId, bulkStatus);
      });
    };

    const allItemsDone = order.items.every((item) => item.status === 'completed' || item.status === 'rejected');

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.2 }}
      >
        <Card className="p-2.5 sm:p-3 bg-white shadow-sm sm:shadow-md rounded-lg border border-gray-100 hover:shadow-md transition-all flex flex-col">
          <div className="flex flex-col gap-2 flex-grow">
            {/* رأس الطلب */}
            <div className="flex flex-row justify-between items-start sm:items-center gap-1.5">
              <div>
                <h2 className="text-sm sm:text-base font-semibold text-gray-800">
                  {t('orders.order_number')}: {order.orderNumber}
                </h2>
                <p className="text-[10px] sm:text-xs text-gray-600">
                  {t('orders.created_at')}: {formatDate(order.createdAt, language)}
                </p>
              </div>
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-medium ${
                  order.status === 'completed'
                    ? 'bg-green-100 text-green-800'
                    : order.status === 'rejected'
                    ? 'bg-red-100 text-red-800'
                    : order.status === 'in_production'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-amber-100 text-amber-800'
                }`}
              >
                {t(`orders.status_${order.status}`)}
              </span>
            </div>

            {/* شريط التقدم */}
            <div className="mt-1.5">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full transition-all duration-500 ${
                    progressPercentage === 100 ? 'bg-green-500' : 'bg-gradient-to-r from-blue-500 to-blue-600'
                  }`}
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <p className="text-[10px] flex justify-end text-gray-600 mt-1">
                {completedItems} / {totalItems} {t('orders.completed')}
                {rejectedItems > 0 && `, ${rejectedItems} ${t('orders.rejected')}`}
              </p>
            </div>

            {/* عناصر الطلب */}
            <div className="space-y-1.5 mt-2 flex-grow">
              <AnimatePresence>
                {order.items.map((item) => {
                  const statusInfo = getStatusInfo(item.status);
                  const { label, color, icon: StatusIcon } = statusInfo;
                  const canUpdate = canUpdateItem(item);
                  const isSelected = selectedItems.has(item._id);
                  const isCompleted = item.status === 'completed';
                  const isRejected = item.status === 'rejected';
                  const isInProgress = item.status === 'in_progress';
                  const isPendingOrAssigned = item.status === 'pending' || item.status === 'assigned';

                  return (
                    <motion.div
                      key={item._id}
                      layout
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div
                        className={`p-2.5 rounded border flex flex-col gap-1.5 ${
                          isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          {!isCompleted && !isRejected && (
                            <button
                              onClick={() => toggleItemSelection(item._id)}
                              className={`p-0.5 rounded ${isSelected ? 'text-blue-600' : 'text-gray-400'}`}
                              aria-label={isSelected ? t('orders.deselect_item') : t('orders.select_item')}
                            >
                              {isSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                            </button>
                          )}

                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-800 text-[13px] truncate">{item.productName}</h4>
                            <span className="text-[10px] text-gray-600">
                              {item.quantity} {item.unit || t('orders.unit')}
                            </span>
                          </div>

                          <div className="flex flex-col items-end gap-1">
                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${color} flex items-center gap-1`}>
                              <StatusIcon className="w-2.5 h-2.5" />
                              {t(`orders.${label}`)}
                            </span>

                            <div className="flex flex-wrap gap-1 justify-end">
                              {isPendingOrAssigned && canUpdate && (
                                <button
                                  onClick={() => handleUpdateItemStatus(order._id, item._id, 'in_progress')}
                                  disabled={isSubmitting(item._id) || !socketConnected}
                                  className="bg-purple-600 hover:bg-purple-700 text-white rounded px-2 py-0.5 text-[10px] shadow-sm transition-all disabled:opacity-50"
                                >
                                  {isSubmitting(item._id) ? (
                                    <LoadingSpinner className="w-2.5 h-2.5" />
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <Play className="w-2.5 h-2.5" />
                                      {isRtl ? 'بدأ' : 'Start'}
                                    </span>
                                  )}
                                </button>
                              )}
                              {isInProgress && canUpdate && (
                                <button
                                  onClick={() => handleUpdateItemStatus(order._id, item._id, 'completed')}
                                  disabled={isSubmitting(item._id) || !socketConnected}
                                  className="bg-green-600 hover:bg-green-700 text-white rounded px-2 py-0.5 text-[10px] shadow-sm transition-all"
                                >
                                  {isSubmitting(item._id) ? (
                                    <LoadingSpinner className="w-2.5 h-2.5" />
                                  ) : (
                                    <span className="flex items-center gap-1">
                                      <Check className="w-2.5 h-2.5" />
                                      {isRtl ? 'إكمال' : 'Complete'}
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>

                            {item.rejected && (
                              <p className="text-[10px] text-red-600 flex items-center gap-1 mt-0.5">
                                <AlertOctagon className="w-2.5 h-2.5" />
                                {t('orders.reject_reason')}: {item.rejectReason || t('orders.not_specified')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* ملاحظات الطلب */}
            {order.notes && (
              <div className="mt-2 p-1.5 bg-blue-50 rounded border border-blue-200">
                <p className="text-[10px] text-blue-800 flex items-start gap-1.5">
                  <AlertCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                  <span>{order.notes}</span>
                </p>
              </div>
            )}
          </div>

          {/* أزرار التحكم الجماعي */}
          <div className="mt-2">
            {!allItemsDone && (
              <div className="flex flex-wrap justify-end items-center gap-1.5">
                <button
                  onClick={toggleSelectAll}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded px-2 py-1 text-[10px] font-medium shadow-sm transition-all"
                >
                  {selectedItems.size === order.items.length ? (
                    <CheckSquare className="w-3 h-3" />
                  ) : (
                    <span className="flex items-center gap-1">
                      <Square className="w-2.5 h-2.5" />
                      {isRtl ? 'تحديد' : 'Select'}
                    </span>
                  )}
                </button>

                {selectedItems.size > 0 && (
                  <>
                    <button
                      onClick={() => openBulkModal('in_progress')}
                      disabled={submittingAll || !socketConnected}
                      className="bg-purple-600 hover:bg-purple-700 text-white rounded px-2 py-1 text-[10px] font-medium shadow-sm transition-all disabled:opacity-50"
                    >
                      <span className="flex items-center gap-1">
                        <Play className="w-2.5 h-2.5" />
                        {isRtl ? 'بدأ' : 'Start'}
                      </span>
                    </button>
                    <button
                      onClick={() => openBulkModal('completed')}
                      disabled={submittingAll || !socketConnected}
                      className="bg-green-600 hover:bg-green-700 text-white rounded px-2 py-1 text-[10px] font-medium shadow-sm transition-all disabled:opacity-50"
                    >
                      <span className="flex items-center gap-1">
                        <Check className="w-2.5 h-2.5" />
                        {isRtl ? 'إكمال' : 'Complete'}
                      </span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* نافذة التحديث الجماعي */}
        <AnimatePresence>
          {showBulkModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg shadow-lg p-3 w-full max-w-sm"
              >
                <h3 className="text-sm font-bold text-gray-800 mb-2">
                  {t('orders.update_selected_items_to', { status: t(`orders.item_${bulkStatus}`) })}
                </h3>
                <div className="flex justify-end gap-2">
                  <Button
                    size="xs"
                    onClick={closeBulkModal}
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded px-2.5 py-1 text-[10px] shadow-sm transition-all"
                  >
                    {isRtl ? 'إلغاء' : 'Cancel'}
                  </Button>
                  <Button
                    size="xs"
                    onClick={executeBulkUpdate}
                    disabled={submittingAll}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1 text-[10px] shadow-sm transition-all disabled:opacity-50"
                  >
                    {submittingAll ? <LoadingSpinner className="w-2.5 h-2.5" /> : isRtl ? 'تأكيد' : 'Confirm'}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);
OrderCard.displayName = 'OrderCard';
// ========== ChefTasks ==========
export function ChefTasks() {
  const { t, language } = useLanguage();
  const isRtl = language === 'ar';
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, { ...initialState, socketConnected: isConnected });
  const stateRef = useRef(state);
  stateRef.current = state;
  const tasksPerPage = 10;
  const taskQueue = useRef<Array<{ taskId: string; orderId: string; status: TaskStatus; rejectReason?: string }>>([]);
  const isProcessingQueue = useRef(false);
  const [submittingAll, setSubmittingAll] = useState(false);

  useOrderNotifications(dispatch, stateRef, user);

  const cacheKey = useMemo(
    () => `${state.chefId}-${state.page}-${state.filter.status}-${state.filter.search}`,
    [state.chefId, state.page, state.filter]
  );

  const fetchChefProfile = useCallback(async () => {
    const userId = user?.id || user?._id;
    if (!userId || !/^[0-9a-fA-F]{24}$/.test(userId)) {
      dispatch({ type: 'SET_ERROR', payload: t('errors.unauthorized') });
      toast.error(t('errors.unauthorized'), { position: isRtl ? 'top-left' : 'top-right' });
      navigate('/login');
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    if (user?.role !== 'chef') {
      dispatch({ type: 'SET_ERROR', payload: t('errors.unauthorized') });
      toast.error(t('errors.unauthorized'), { position: isRtl ? 'top-left' : 'top-right' });
      navigate('/');
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }
    try {
      const chefProfile = await chefsAPI.getByUserId(userId);
      if (!chefProfile?._id || !/^[0-9a-fA-F]{24}$/.test(chefProfile._id)) {
        throw new Error(t('errors.invalid_chef_data'));
      }
      dispatch({ type: 'SET_CHEF_ID', payload: chefProfile._id });
    } catch (err: any) {
      const errorMessage = err.message || t('errors.chef_fetch_failed');
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage, { position: isRtl ? 'top-left' : 'top-right' });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [user, t, isRtl, navigate]);

  const fetchTasks = useCallback(async (forceRefresh = false) => {
    if (!state.chefId || !/^[0-9a-fA-F]{24}$/.test(state.chefId)) {
      dispatch({ type: 'SET_ERROR', payload: t('errors.no_chef_id') });
      toast.error(t('errors.no_chef_id'), { position: isRtl ? 'top-left' : 'top-right' });
      return;
    }

    const cached = cache.get(cacheKey);
    if (!forceRefresh && cached && Date.now() - cached.timestamp < CACHE_TTL) {
      dispatch({
        type: 'SET_TASKS',
        payload: { tasks: cached.data, totalPages: Math.ceil(cached.data.length / tasksPerPage) || 1 },
      });
      return;
    }

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const query: Record<string, any> = { page: state.page, limit: tasksPerPage };
      if (state.filter.status && state.filter.status !== 'all') query.status = state.filter.status;
      if (state.filter.search) query.search = state.filter.search;

      const response = await productionAssignmentsAPI.getChefTasks(state.chefId, query);
      if (!Array.isArray(response)) throw new Error(t('errors.invalid_response'));

      const mappedTasks: ChefTask[] = response
        .filter((task: any) => {
          if (!task._id || !task.order?._id || !task.product?._id || !task.chef?._id) return false;
          if (
            !/^[0-9a-fA-F]{24}$/.test(task._id) ||
            !/^[0-9a-fA-F]{24}$/.test(task.order._id) ||
            !/^[0-9a-fA-F]{24}$/.test(task.product._id) ||
            !/^[0-9a-fA-F]{24}$/.test(task.chef._id)
          )
            return false;
          return task.quantity > 0;
        })
        .map((task: any) => ({
          itemId: task._id,
          orderId: task.order._id,
          orderNumber: task.order.orderNumber || t('orders.unknown'),
          productId: task.product._id,
          productName: task.product.name || t('orders.unknown_product'),
          quantity: Number(task.quantity),
          status: task.status || 'pending',
          rejected: task.rejected || false,
          rejectReason: (task.rejectReason || 'أخرى') as RejectReason,
          createdAt: task.createdAt || new Date().toISOString(),
          updatedAt: task.updatedAt || new Date().toISOString(),
          progress: getStatusInfo(task.status || 'pending').progress,
          branchName: task.order?.branch?.name || t('orders.unknown_branch'),
          branchId: task.order?.branch?._id,
          priority: task.order?.priority || 'medium',
          department: task.product?.department || { _id: 'unknown', name: t('orders.unknown_department') },
          assignedTo: task.chef ? { _id: task.chef._id, username: task.chef.username || t('orders.unknown_chef') } : undefined,
          unit: task.unit || t('orders.unit'),
        }));

      cache.set(cacheKey, { data: mappedTasks, timestamp: Date.now() });
      dispatch({
        type: 'SET_TASKS',
        payload: { tasks: mappedTasks, totalPages: Math.ceil(mappedTasks.length / tasksPerPage) || 1 },
      });
      dispatch({ type: 'SET_ERROR', payload: '' });
    } catch (err: any) {
      const errorMessage = err.message || t('errors.task_fetch_failed');
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage, { position: isRtl ? 'top-left' : 'top-right' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.chefId, state.page, state.filter, cacheKey, t, isRtl]);

  const updateSingleItemStatus = useCallback(
    async (orderId: string, itemId: string, newStatus: TaskStatus, rejectReason?: string): Promise<void> => {
      // ✅ تحقق مزدوج: تجاهل إذا كان مكتمل أو مرفوض
      const existingTask = state.tasks.find((t) => t.itemId === itemId && t.orderId === orderId);
      if (!existingTask || existingTask.status === 'completed' || existingTask.status === 'rejected') {
        console.log(`[SKIP] Item ${itemId} is already finalized.`);
        return;
      }

      if (!state.chefId || !user?.id) {
        throw new Error(t('errors.unauthorized'));
      }

      dispatch({ type: 'SET_SUBMITTING', payload: { itemId, add: true } });
      try {
        const payload = rejectReason ? { status: newStatus, rejectReason } : { status: newStatus };
        const response = await productionAssignmentsAPI.updateTaskStatus(orderId, itemId, payload);
        dispatch({
          type: 'UPDATE_ITEM_STATUS',
          payload: {
            orderId,
            itemId,
            status: response.task.status,
            updatedAt: new Date().toISOString(),
            rejected: response.task.rejected,
            rejectReason: response.task.rejectReason,
          },
        });

        if (state.socketConnected) {
          socket.emit('itemStatusUpdated', {
            orderId,
            itemId,
            status: newStatus,
            chefId: state.chefId,
            productName: existingTask.productName,
            orderNumber: existingTask.orderNumber,
            branchName: existingTask.branchName,
            quantity: existingTask.quantity,
            unit: existingTask.unit,
            eventId: crypto.randomUUID(),
          });
        }
      } catch (err: any) {
        const errorMessage = err.message || t('errors.item_update_failed');
        dispatch({ type: 'SET_ERROR', payload: errorMessage });
        toast.error(errorMessage, { position: isRtl ? 'top-left' : 'top-right' });
        throw err;
      } finally {
        dispatch({ type: 'SET_SUBMITTING', payload: { itemId, add: false } });
      }
    },
    [state.tasks, state.chefId, user, socket, state.socketConnected, t, isRtl]
  );

  const handleUpdateItemStatus = useCallback(
    (orderId: string, itemId: string, newStatus: TaskStatus, rejectReason?: string) => {
      const task = state.tasks.find((t) => t.itemId === itemId && t.orderId === orderId);
      if (!task || task.status === 'completed' || task.status === 'rejected') return;

      taskQueue.current.push({ taskId: itemId, orderId, status: newStatus, rejectReason });
      if (!isProcessingQueue.current) {
        isProcessingQueue.current = true;
        const process = async () => {
          while (taskQueue.current.length > 0) {
            const task = taskQueue.current.shift();
            if (task) {
              try {
                await updateSingleItemStatus(task.orderId, task.taskId, task.status, task.rejectReason);
                await new Promise((resolve) => setTimeout(resolve, 50));
              } catch (e) {
                console.warn(`Failed to process queued item ${task.taskId}`, e);
              }
            }
          }
          isProcessingQueue.current = false;
        };
        process();
      }
    },
    [state.tasks, updateSingleItemStatus]
  );

  useEffect(() => {
    fetchChefProfile();
  }, [fetchChefProfile]);

  useEffect(() => {
    if (state.chefId) {
      fetchTasks();
    }
  }, [state.chefId, fetchTasks]);

  useEffect(() => {
    dispatch({ type: 'SET_SOCKET_CONNECTED', payload: isConnected });
  }, [isConnected]);

  const groupedOrders = useMemo(() => {
    const grouped: Record<string, any> = {};
    state.tasks.forEach((task) => {
      if (!grouped[task.orderId]) {
        grouped[task.orderId] = {
          _id: task.orderId,
          orderNumber: task.orderNumber,
          createdAt: task.createdAt,
          status: 'pending',
          items: [],
          notes: '',
          branch: task.branchId,
        };
      }
      grouped[task.orderId].items.push({
        _id: task.itemId,
        productName: task.productName,
        quantity: task.quantity,
        status: task.status,
        rejected: task.rejected,
        rejectReason: task.rejectReason,
        createdAt: task.createdAt,
        updatedAt: task.updatedAt,
        assignedTo: task.assignedTo?._id,
        unit: task.unit,
      });
    });

    Object.values(grouped).forEach((order: any) => {
      if (order.items.some((i: any) => i.status === 'rejected')) {
        order.status = 'rejected';
      } else if (order.items.every((i: any) => i.status === 'completed')) {
        order.status = 'completed';
      } else if (order.items.some((i: any) => i.status === 'in_progress')) {
        order.status = 'in_production';
      } else {
        order.status = 'assigned';
      }
    });

    return Object.values(grouped);
  }, [state.tasks]);

  const filteredOrders = useMemo(() => {
    return groupedOrders.filter((order: any) => {
      const matchesStatus = state.filter.status === 'all' || order.status === state.filter.status;
      const matchesSearch =
        order.orderNumber.toLowerCase().includes(state.filter.search.toLowerCase()) ||
        order.items.some((item: any) => item.productName.toLowerCase().includes(state.filter.search.toLowerCase()));
      return matchesStatus && matchesSearch;
    });
  }, [groupedOrders, state.filter]);

  const paginatedOrders = useMemo(() => {
    const start = (state.page - 1) * tasksPerPage;
    return filteredOrders.slice(start, start + tasksPerPage);
  }, [filteredOrders, state.page]);

  const totalPages = useMemo(() => Math.ceil(filteredOrders.length / tasksPerPage), [filteredOrders]);

  const statusOptions = [
    { value: 'all', label: t('orders.all_statuses') },
    { value: 'pending', label: t('orders.status_pending') },
    { value: 'in_production', label: t('orders.status_in_production') },
    { value: 'completed', label: t('orders.status_completed') },
    { value: 'rejected', label: t('orders.status_rejected') },
  ];

  const SkeletonCard = () => (
    <Card className="p-3 sm:p-4 bg-white shadow-md rounded-lg border border-gray-100 animate-pulse w-full">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          </div>
          <div className="space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-3 bg-gray-100 rounded-lg h-12 sm:h-14"></div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className={`px-1 sm:px-2 lg:px-3 py-2 sm:py-3 min-h-screen w-full`}>
      <Card className="mb-4 bg-white shadow-md rounded-lg p-3 w-full">
        <div className="flex flex-col lg:flex-row gap-2">
          <div className="lg:w-2/3 w-full">
            <ProductSearchInput
              value={state.filter.search}
              onChange={(e) => dispatch({ type: 'SET_FILTER', payload: { ...state.filter, search: e.target.value } })}
              placeholder={t('orders.search_placeholder')}
              ariaLabel={t('orders.search')}
              className="w-full text-xs"
            />
          </div>
          <div className="lg:w-1/3 w-full">
            <ProductDropdown
              options={statusOptions}
              value={state.filter.status}
              onChange={(value) => dispatch({ type: 'SET_FILTER', payload: { ...state.filter, status: value } })}
              ariaLabel={t('orders.filter_by_status')}
            />
          </div>
        </div>
      </Card>

      {state.loading ? (
        <div className="flex flex-col gap-2 w-full">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : state.error ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full"
        >
          <Card className="p-3 text-center bg-red-50 shadow-md rounded-lg border border-red-200">
            <div className="flex items-center justify-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-xs font-medium text-red-600">{state.error}</p>
            </div>
            <Button
              variant="primary"
              onClick={() => {
                cache.delete(cacheKey);
                fetchChefProfile();
                if (state.chefId) fetchTasks(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-1.5 text-xs shadow-sm transition-all"
              aria-label={t('orders.retry')}
            >
              {t('orders.retry')}
            </Button>
          </Card>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full"
        >
          {!state.socketConnected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm flex items-center gap-2"
            >
              <AlertCircle className="w-3 h-3 text-yellow-600" />
              <p className="text-xs text-yellow-700">{t('errors.socket_disconnected')}</p>
            </motion.div>
          )}

          <AnimatePresence>
            {paginatedOrders.length === 0 ? (
              <Card className="p-4 text-center bg-white shadow-md rounded-lg border border-gray-100 w-full">
                <p className="text-xs sm:text-sm text-gray-600">{t('orders.no_orders')}</p>
              </Card>
            ) : (
              <div className="space-y-3 w-full">
                {paginatedOrders.map((order: any) => (
                  <OrderCard
                    key={order._id}
                    order={order}
                    isRtl={isRtl}
                    t={t}
                    submittingItems={state.submitting}
                    submittingAll={submittingAll}
                    chefId={state.chefId}
                    socketConnected={state.socketConnected}
                    language={language}
                    handleUpdateItemStatus={handleUpdateItemStatus}
                  />
                ))}
              </div>
            )}
          </AnimatePresence>

          {totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center items-center gap-2 mt-4 w-full"
            >
              <button
                onClick={() => dispatch({ type: 'SET_PAGE', payload: state.page - 1 })}
                disabled={state.page === 1}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-xs shadow-sm transition-all disabled:opacity-50"
                aria-label={t('orders.previous')}
              >
                {t('orders.previous')}
              </button>
              <span className="text-xs text-gray-700 font-semibold">
                {t('pagination.page', { current: state.page, total: totalPages })}
              </span>
              <button
                onClick={() => dispatch({ type: 'SET_PAGE', payload: state.page + 1 })}
                disabled={state.page === totalPages}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg px-3 py-1.5 text-xs shadow-sm transition-all disabled:opacity-50"
                aria-label={t('orders.next')}
              >
                {t('orders.next')}
              </button>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export default ChefTasks;